"use client";

import { useEffect, useState } from "react";
import { useCart, useUI } from "@/lib/store";
import { formatPrice } from "@/lib/data";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Phone, ShoppingBag, Trash2, Crown, Menu } from "lucide-react";
import Image from "next/image";

// I) Menu : chaque lien ouvre sa boutique dédiée (dialog)
const navLinks = [
  { label: "Offres", href: "#promos" },
  { label: "Mobile", href: "#mondes", monde: "mobile" },
  { label: "TV & AV", href: "#mondes", monde: "tv-av" },
  { label: "Électroménager", href: "#mondes", monde: "electro" },
  { label: "Objets Connectés", href: "#mondes", monde: "iot" },
  { label: "Univers Tech", href: "#univers-tech" },
  { label: "Solutions Pro", href: "#solutions-pro" },
  { label: "Présidentiel", href: "#presidentiel", crown: true },
];

export function Header() {
  const { items, isOpen, openCart, closeCart, removeItem, total, count } = useCart();
  const openBoutique = useUI((s) => s.openBoutique);
  const openCheckout = useUI((s) => s.openCheckout);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const cartCount = count();

  // I) Navigation menu → ouverture de la boutique du monde correspondant
  const handleNav = (e: React.MouseEvent, link: (typeof navLinks)[number]) => {
    if (link.monde) {
      e.preventDefault();
      openBoutique(link.monde);
      setMobileOpen(false);
    } else {
      setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Bannière TERANGA */}
      <div className="relative overflow-hidden text-white text-center py-2.5 px-4 z-[60] bg-[#006B3F]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#006B3F] via-[#007A48] to-[#006B3F]" />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#006B3F] via-[#FFD700] to-[#CE1126]" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#006B3F] via-[#FFD700] to-[#CE1126]" />
        <span className="relative inline-flex items-center gap-2.5 text-xs sm:text-sm font-medium tracking-wide flex-wrap justify-center">
          <span className="relative w-2 h-2 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] shadow-[0_0_6px_rgba(255,215,0,0.5)]" />
          <span className="text-[#FFD700] text-base drop-shadow-[0_0_4px_rgba(255,215,0,0.7)]">★</span>
          <span className="font-bold tracking-[0.15em] bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">
            TERANGA
          </span>
          <span className="text-white/60 hidden sm:inline">|</span>
          <span className="text-white/90 hidden sm:inline">
            Livraison Concierge · Garantie 3 Ans · 6x sans frais · Concierge 24/7
          </span>
          <span className="text-white/90 sm:hidden">Services VIP inclus</span>
        </span>
      </div>

      {/* Navigation */}
      <nav
        className={`sticky top-0 z-[55] transition-all duration-500 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-[76px]">
            <a href="#" className="flex items-center gap-3 group cursor-pointer">
              <div className="relative w-11 h-11 sm:w-14 sm:h-14 bg-gradient-to-br from-[#006B3F] to-[#008751] rounded-2xl flex items-center justify-center shadow-xl shadow-[#FFD700]/10 group-hover:shadow-2xl transition-all border border-[#FFD700]/30">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full border-2 border-white" />
              </div>
              <div className="leading-tight">
                <div className="font-black text-[#0A0A0A] text-sm sm:text-lg tracking-tight">
                  SENEGAL TELECOM <span className="text-[#006B3F]">STORE</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] sm:text-[10px] font-black tracking-[0.3em] bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">
                    LUXE
                  </span>
                  <span className="h-[1px] w-6 bg-gradient-to-r from-[#FFD700] to-transparent" />
                </div>
              </div>
            </a>

            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNav(e, link)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    link.crown
                      ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50 flex items-center gap-1.5"
                      : "text-gray-700 hover:text-[#006B3F] hover:bg-green-50"
                  }`}
                >
                  {link.crown && <Crown className="w-3.5 h-3.5" />}
                  {link.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openCart}
                className="relative border-[#006B3F]/30 text-[#006B3F] hover:bg-[#006B3F] hover:text-white"
              >
                <ShoppingBag className="w-4 h-4" />
                <span className="hidden sm:inline ml-1.5">Panier</span>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] text-[10px] font-bold text-black flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Menu"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {mobileOpen && (
            <div className="lg:hidden pb-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNav(e, link)}
                  className={`px-4 py-2.5 text-sm font-medium rounded-lg cursor-pointer ${
                    link.crown ? "text-amber-600 bg-amber-50" : "text-gray-700 hover:bg-green-50"
                  }`}
                >
                  {link.crown ? "👑 " : ""}
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
        {/* Liseré drapeau sénégalais sous la navigation — visible en permanence */}
        <div className="h-[3px] bg-gradient-to-r from-[#006B3F] via-[#FFD700] to-[#CE1126]" />
      </nav>

      {/* Panier */}
      <Sheet open={isOpen} onOpenChange={(o) => (o ? openCart() : closeCart())}>
        <SheetContent className="w-full sm:max-w-md flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#006B3F]" />
              Votre Panier ({cartCount})
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4">
            {items.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Votre panier est vide</p>
                <p className="text-xs mt-1">Découvrez nos collections d&apos;exception</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3 py-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-3 p-3 rounded-xl border border-gray-100 shadow-sm bg-white"
                  >
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-gray-50">
                      <Image src={item.image} alt={item.name} fill className="object-cover" sizes="64px" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {item.presidential && "👑 "}
                        {item.name}
                      </p>
                      <p className="text-sm font-bold text-[#006B3F]">
                        {formatPrice(item.price)} FCFA × {item.quantity}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="self-center p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                      aria-label="Retirer du panier"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {items.length > 0 && (
            <div className="border-t border-gray-100 p-4 space-y-3">
              <div className="flex justify-between text-sm text-gray-500">
                <span>Sous-total</span>
                <span className="font-semibold text-gray-900">{formatPrice(total())} FCFA</span>
              </div>
              <div className="flex justify-between text-sm text-gray-500">
                <span>Paiement 6x sans frais</span>
                <span className="font-semibold text-[#006B3F]">
                  {formatPrice(Math.round(total() / 6))} FCFA/mois
                </span>
              </div>
              <Button
                className="w-full bg-gradient-to-r from-[#006B3F] to-[#008751] text-white h-11"
                onClick={() => {
                  // VII) Paiement : ouverture du tunnel de commande Concierge
                  closeCart();
                  setTimeout(() => openCheckout(), 250);
                }}
              >
                Commander avec Concierge
              </Button>
              <p className="text-[11px] text-center text-gray-400">
                Garantie 3 ans · Livraison Concierge Blanche · Concierge 24/7
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
